import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GpsFields } from "@/components/GpsFields";
import { GpsUnitCard } from "@/components/gps/GpsUnitCard";
import { UnitPhotosButton } from "@/components/gps/UnitPhotosButton";
import { api, errMsg } from "@/lib/api";
import { blankUnit, gpsFlow, hasLegacyGps, slotsFor, toUnitPayload } from "@/lib/gps";

const withSlots = (u) => ({ ...u, key: u.id, devices: slotsFor(u.plan, u.devices) });

const Legacy = ({ gps }) => (
  <div className="space-y-2 rounded-xl border border-dashed border-border p-4" data-testid="gps-legacy-block">
    <p className="text-sm text-muted-foreground">Datos capturados con el formato anterior (una unidad, sin plan). Se conservan sin cambios.</p>
    <GpsFields value={gps} disabled onChange={() => {}} />
  </div>
);

export const GpsUnitsEditor = ({ woId, gps, units: localUnits, onChange, onSaved, editable, techMode = false, serviceType, technicianId }) => {
  const remote = !!woId;
  const flow = gpsFlow(serviceType);
  const [plans, setPlans] = useState([]);
  const [drafts, setDrafts] = useState(() => (gps?.units || []).map(withSlots));
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/gps-plans").then((r) => setPlans(r.data)); }, []);
  const list = remote ? drafts : localUnits;
  const setList = remote ? setDrafts : onChange;
  const update = (i, u) => setList(list.map((x, j) => (j === i ? { ...u, dirty: true } : x)));

  const persist = async (i, u) => {
    if (flow.install && !u.plan) return toast.error("Selecciona el plan de la unidad");
    if (flow.remove && !techMode && !u.target) return toast.error("Selecciona el equipo GPS a retirar o registra uno no registrado");
    if (flow.reinstall && !techMode && !u.source_device) return toast.error("Selecciona el equipo en resguardo TSSMX a reinstalar");
    setBusy(u.key);
    try {
      const { data } = u.id ? await api.put(`/work-orders/${woId}/gps/units/${u.id}`, toUnitPayload(u)) : await api.post(`/work-orders/${woId}/gps/units`, toUnitPayload(u));
      const saved = u.id ? data.find((x) => x.id === u.id) : data[data.length - 1];
      setDrafts((ds) => ds.map((x) => (x.key === u.key ? withSlots(saved) : x)));
      onSaved?.(data);
      toast.success(`Unidad ${i + 1} guardada`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const remove = async (u) => {
    if (remote && u.id) {
      if (!window.confirm("¿Eliminar esta unidad GPS de la OS?")) return;
      try { const { data } = await api.delete(`/work-orders/${woId}/gps/units/${u.id}`); onSaved?.(data); } catch (e) { return toast.error(errMsg(e)); }
    }
    setList(list.filter((x) => x.key !== u.key));
  };

  const saveBtn = (i, u) => remote && <Button onClick={() => persist(i, u)} disabled={busy === u.key || (u.id && !u.dirty)} className={techMode ? "h-12 w-full text-base" : ""} data-testid={`gps-unit-${i}-save`}>{busy === u.key ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}{u.id ? "Guardar unidad" : "Guardar nueva unidad"}</Button>;

  return (
    <div className="space-y-4" data-testid="gps-units-editor">
      {!techMode && hasLegacyGps(gps) && <Legacy gps={gps} />}
      {list.map((u, i) => (
        <GpsUnitCard key={u.key} woId={woId} technicianId={technicianId} n={i} unit={u} plans={plans} flow={flow} editable={editable} configEditable={editable && !techMode} planLocked={techMode && !!u.id} onChange={(nu) => update(i, nu)}
          footer={woId && <>{techMode && u.id && <UnitPhotosButton woId={woId} unit={u} n={i} editable={editable} />}{editable && <div className={techMode ? "" : "flex justify-end border-t border-border pt-4"}>{saveBtn(i, u)}</div>}</>}
          actions={editable && (
            <div className="flex gap-2">
              {(!techMode || !u.id) && <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`gps-unit-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          )} />
      ))}
      {!list.length && (techMode || !hasLegacyGps(gps)) && <p className="text-sm text-muted-foreground" data-testid="gps-units-empty">Sin unidades GPS.</p>}
      {editable && !(techMode && flow.remove) && <Button variant="outline" onClick={() => setList([...list, blankUnit()])} data-testid="gps-add-unit-button"><Plus className="mr-1 h-4 w-4" />Agregar vehículo / unidad</Button>}
      {remote && list.some((u) => u.dirty || !u.id) && <p className="text-xs text-amber-300" data-testid="gps-units-unsaved">Hay unidades con cambios sin guardar.</p>}
    </div>
  );
};
